// The answer cards of a choice question (a radio group). Arrow keys move between cards, Space or a click chooses,
// Enter on the chosen card checks, and the number keys 1–4 choose directly. After a wrong answer the right card
// is marked. Prakrit options are plain text inside the buttons (no nested Word Explorer buttons).

import type { FC, ReactNode } from 'react'
import type { AnswerResponse, ChoiceOption } from '@/prakrit/components/practice/practice-types'

import { Check, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Bi } from '@/prakrit/components/common/bi'
import { moveBetween } from '@/prakrit/components/lesson/steps/step-ui'

type CardState = 'idle' | 'selected' | 'correct' | 'wrong' | 'reveal'

const CARD: Record<CardState, string> = {
    idle: 'border-border bg-card hover:border-saffron/60 hover:bg-saffron/5',
    selected: 'border-saffron bg-saffron/10',
    correct: 'border-success bg-success-soft',
    wrong: 'animate-shake-x border-danger bg-danger-soft',
    reveal: 'border-dashed border-success bg-card'
}

const BADGE: Record<CardState, string> = {
    idle: 'border-border bg-background text-muted-foreground',
    selected: 'border-saffron bg-saffron text-foreground',
    correct: 'border-success bg-success text-white dark:text-background',
    wrong: 'border-danger bg-danger text-white dark:text-background',
    reveal: 'border-success bg-success-soft text-success'
}

interface ChoiceOptionsProps {
    options: ChoiceOption[]
    value: string | null
    onChange: (id: string) => void
    locked: boolean
    result: AnswerResponse | null
    kids: boolean
    label: string
}

const textOf = (o: ChoiceOption): string => o.pra ?? o.sa ?? ''

export const ChoiceOptions: FC<ChoiceOptionsProps> = ({ options, value, onChange, locked, result, kids, label }): ReactNode => {
    const sentences = options.some(o => textOf(o).includes(' '))
    const labels = options.every(o => !o.pra && !o.sa)
    const twoColumns = !sentences && (kids || options.length >= 4 || labels)

    return (
        <div
            role='radiogroup'
            aria-label={label}
            onKeyDown={event => moveBetween(event)}
            className={cn('grid gap-2.5', twoColumns && 'sm:grid-cols-2', kids && !sentences && 'grid-cols-2 gap-3')}
        >
            {options.map((option, i) => {
                const selected = value === option.id
                let state: CardState = selected ? 'selected' : 'idle'

                if (result) {
                    if (selected) state = result.correct ? 'correct' : 'wrong'
                    else if (!result.correct && result.expectedId === option.id) state = 'reveal'
                }

                return (
                    <button
                        key={option.id}
                        type='button'
                        role='radio'
                        aria-checked={selected}
                        aria-disabled={locked || undefined}
                        aria-keyshortcuts={String(i + 1)}
                        data-option
                        data-enter-submit
                        onClick={() => {
                            if (!locked) onChange(option.id)
                        }}
                        className={cn(
                            'flex min-h-tap w-full items-center gap-3 rounded-xl border-2 px-4 py-3 text-left shadow-sm transition-colors',
                            kids && !sentences && 'min-h-24 flex-col justify-center gap-2 text-center',
                            CARD[state],
                            locked && state === 'idle' && 'opacity-60 hover:border-border hover:bg-card'
                        )}
                    >
                        <span
                            aria-hidden='true'
                            className={cn('flex size-7 shrink-0 items-center justify-center rounded-full border text-sm font-semibold', BADGE[state])}
                        >
                            {state === 'correct' || state === 'reveal' ? <Check className='size-4' /> : state === 'wrong' ? <X className='size-4' /> : i + 1}
                        </span>
                        <span className='flex min-w-0 flex-1 flex-col gap-0.5 break-words'>
                            {option.pra &&<span lang='pra' className={cn('pra', sentences ? 'pra-md' : 'pra-lg')}>{option.pra}</span>}
                            {option.sa && <span lang='sa' className='font-prakrit text-2xl font-semibold text-foreground'>{option.sa}</span>}
                            {!option.pra && !option.sa && (
                                <Bi hi={option.hi ?? ''} en={option.en ?? ''} hiClassName='text-lg font-medium' enClassName='text-sm' />
                            )}
                        </span>
                        {state === 'reveal' && (
                            <span className='shrink-0 text-xs font-semibold text-success'>
                                <span lang='hi' className='hindi'>सही उत्तर</span>
                                <span className='sr-only'> · correct answer</span>
                            </span>
                        )}
                    </button>
                )
            })}
        </div>
    )
}
